import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  FaPlus,
  FaTrashAlt,
  FaSyncAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInstagram,
  FaFacebook,
  FaLinkedin,
  FaYoutube,
  FaTwitter,
  FaTiktok,
} from 'react-icons/fa';
import {
  TechSection,
  TechInput,
  TechSelect,
  TechButton,
  TechButtonGhost,
  TechButtonDanger,
  TechChip,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  Empty,
  Loading,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import {
  listSocialProfiles,
  createSocialProfile,
  deleteSocialProfile,
  listSocialRuns,
  addManualSnapshot,
} from '../../services/platformService';
import { FaEdit } from 'react-icons/fa';

const Row = styled.div`
  display: grid;
  grid-template-columns: ${({ cols }) => `repeat(${cols || 4}, 1fr)`};
  gap: 0.55rem;
  margin-bottom: 0.85rem;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Card = styled.article`
  display: grid;
  grid-template-columns: 30px 1fr auto auto;
  gap: 0.7rem;
  align-items: center;
  padding: 0.7rem 0.85rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${({ $color }) => $color || accent};
  border-radius: 5px;
  margin-bottom: 0.45rem;

  &:last-child { margin-bottom: 0; }

  .icon {
    width: 30px;
    height: 30px;
    border-radius: 6px;
    background: ${({ $color }) => `${$color}22` || 'rgba(0,180,216,0.1)'};
    color: ${({ $color }) => $color || accent};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
  }

  .name {
    color: var(--text-primary);
    font-size: 0.92rem;
    font-weight: 500;
    margin: 0;
  }

  .meta {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-top: 2px;
  }

  .stats {
    text-align: right;
    font-family: ${mono};
    font-size: 0.85rem;
    color: var(--text-primary);

    .delta {
      display: block;
      font-size: 0.7rem;
      color: var(--text-disabled);
      margin-top: 2px;
    }
  }
`;

const PLATFORM_META = {
  instagram: { color: '#E1306C', icon: <FaInstagram /> },
  facebook:  { color: '#4267B2', icon: <FaFacebook /> },
  x:         { color: '#1DA1F2', icon: <FaTwitter /> },
  twitter:   { color: '#1DA1F2', icon: <FaTwitter /> },
  tiktok:    { color: '#FF0050', icon: <FaTiktok /> },
  linkedin:  { color: '#0077B5', icon: <FaLinkedin /> },
  youtube:   { color: '#FF0000', icon: <FaYoutube /> },
};

const fmtRel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d.getTime();
  const h = diff / 36e5;
  if (h < 1) return 'hace <1h';
  if (h < 24) return `hace ${Math.round(h)}h`;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
};

const fmtNum = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const SocialPanel = () => {
  const [profiles, setProfiles] = useState([]);
  const [runsData, setRunsData] = useState({ runs: [], totals: {}, schedule: null });
  const [loadState, setLoadState] = useState({ loading: true, error: null });
  const [form, setForm] = useState({ platform: 'instagram', handle: '', display_name: '', kind: 'profile', is_own: true });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoadState({ loading: true, error: null });
    Promise.all([listSocialProfiles().catch(() => []), listSocialRuns().catch(() => ({ runs: [] }))])
      .then(([p, r]) => {
        setProfiles(p);
        setRunsData(r);
        setLoadState({ loading: false, error: null });
      })
      .catch((e) => setLoadState({ loading: false, error: e?.message || 'Error' }));
  };

  useEffect(load, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.platform || !form.handle.trim()) return;
    setBusy(true);
    try {
      await createSocialProfile({
        platform: form.platform,
        handle: form.handle.trim().replace(/^@/, ''),
        display_name: form.display_name || null,
        kind: form.kind,
        is_own: form.is_own,
      });
      setForm({ ...form, handle: '', display_name: '' });
      load();
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar perfil y todos sus snapshots?')) return;
    await deleteSocialProfile(id);
    load();
  };

  const handleManual = async (profile) => {
    const raw = window.prompt(`Followers actuales para ${profile.platform} @${profile.handle}`, profile.latest?.followers ?? '');
    if (raw == null) return;
    const n = parseInt(String(raw).replace(/[^\d]/g, ''), 10);
    if (!Number.isFinite(n) || n < 0) {
      alert('Número inválido');
      return;
    }
    try {
      await addManualSnapshot(profile.id, { followers: n, notes: 'Carga manual desde panel' });
      load();
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Error');
    }
  };

  const lastRun = runsData.runs?.[0];

  if (loadState.loading) return <Loading>Cargando perfiles</Loading>;

  return (
    <>
      <StatStrip min="170px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel>Perfiles registrados</StatLabel>
          <StatValue>{profiles.length}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel>Snapshots totales</StatLabel>
          <StatValue>{runsData.totals?.snapshots ?? 0}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel>Última corrida</StatLabel>
          <StatValue style={{ fontSize: '0.95rem' }}>{fmtRel(lastRun?.started_at)}</StatValue>
          <StatSub>+{lastRun?.snapshots_added ?? 0} snapshots</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel>Próxima corrida</StatLabel>
          <StatValue style={{ fontSize: '0.95rem' }}>{fmtRel(runsData.schedule?.next_run_at)}</StatValue>
          <StatSub>{runsData.schedule?.cron || '—'}</StatSub>
        </StatTile>
      </StatStrip>

      <TechSection title="Sumar perfil social" style={{ marginBottom: '1.25rem' }}>
        <form onSubmit={handleAdd}>
          <Row cols={5}>
            <TechSelect value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="x">X (Twitter)</option>
              <option value="tiktok">TikTok</option>
              <option value="linkedin">LinkedIn</option>
              <option value="youtube">YouTube</option>
              <option value="telegram">Telegram</option>
            </TechSelect>
            <TechSelect value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              <option value="profile">Perfil personal</option>
              <option value="page">Page / medio</option>
              <option value="group">Grupo</option>
              <option value="channel">Canal</option>
              <option value="hashtag">Hashtag</option>
            </TechSelect>
            <TechInput placeholder="handle (sin @)" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} />
            <TechInput placeholder="Nombre a mostrar (opcional)" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={form.is_own} onChange={(e) => setForm({ ...form, is_own: e.target.checked })} />
              perfil propio
            </label>
          </Row>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <TechButtonGhost type="button" onClick={load}><FaSyncAlt /> Refrescar</TechButtonGhost>
            <TechButton type="submit" disabled={busy}><FaPlus /> {busy ? '…' : 'Agregar perfil'}</TechButton>
          </div>
        </form>
      </TechSection>

      <TechSection title={`Perfiles · ${profiles.length}`} style={{ marginBottom: '1.25rem' }}>
        {profiles.length === 0 ? (
          <Empty>Aún no hay perfiles. Agregá uno arriba para empezar a medir sus métricas a diario.</Empty>
        ) : (
          profiles.map((p) => {
            const meta = PLATFORM_META[p.platform] || { color: accent, icon: '?' };
            const followers = p.latest?.followers;
            const prevFollowers = p.previous?.followers;
            const delta = followers != null && prevFollowers != null ? followers - prevFollowers : null;
            const status = p.latest?.status;
            return (
              <Card key={p.id} $color={meta.color}>
                <div className="icon">{meta.icon}</div>
                <div>
                  <h4 className="name">{p.display_name || `@${p.handle}`}</h4>
                  <div className="meta">
                    {p.platform} · {p.kind || 'profile'} · @{p.handle}
                    {p.is_own && <TechChip tone="success" style={{ marginLeft: 6, fontSize: '0.58rem' }}>propio</TechChip>}
                    {' · '}
                    {status === 'ok'
                      ? <span style={{ color: 'var(--alert-success)' }}><FaCheckCircle /> {fmtRel(p.latest?.snapshot_at)}</span>
                      : status === 'error'
                        ? <span style={{ color: 'var(--alert-error)' }}><FaExclamationTriangle /> sin acceso</span>
                        : 'sin scrapear todavía'}
                    {p.posts_count > 0 && ` · ${p.posts_count} posts`}
                  </div>
                </div>
                <div className="stats">
                  <span>{fmtNum(followers)}</span>
                  <span className="delta">
                    {delta != null
                      ? (delta > 0 ? `+${fmtNum(delta)}` : delta < 0 ? `−${fmtNum(-delta)}` : '=')
                      : 'seguidores'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <TechButtonGhost
                    type="button"
                    onClick={() => handleManual(p)}
                    style={{ padding: '0.35rem 0.6rem', fontSize: '0.72rem' }}
                    title="Cargar followers manualmente"
                  >
                    <FaEdit />
                  </TechButtonGhost>
                  <TechButtonDanger type="button" onClick={() => handleDelete(p.id)} style={{ padding: '0.35rem 0.6rem', fontSize: '0.72rem' }}>
                    <FaTrashAlt />
                  </TechButtonDanger>
                </div>
              </Card>
            );
          })
        )}
      </TechSection>

      <div style={{
        marginTop: 14,
        padding: '0.85rem 1rem',
        background: panelDeep,
        border: `1px solid ${borderSoft}`,
        borderLeft: `2px solid ${accent}`,
        borderRadius: 6,
        fontSize: '0.85rem',
        color: 'var(--text-secondary)',
        lineHeight: 1.55,
      }}>
        <strong style={{ color: 'var(--text-primary)' }}>Cómo funciona la captura:</strong>
        <ul style={{ margin: '8px 0 0 1.2rem', padding: 0, fontSize: '0.84rem' }}>
          <li><strong>IG</strong>: endpoint <code style={{ fontFamily: mono, color: accent }}>web_profile_info</code> via curl. Trae followers, posts, bio y los últimos 12 posts con likes y comments_count. Sin comentarios reales (requiere cookies dummy en RSSHub).</li>
          <li><strong>YouTube</strong>: feed XML para videos + <strong>Innertube API</strong> (scraping del endpoint privado que usa el browser) para comentarios reales — sin API key, sin signup. Con <code style={{ fontFamily: mono, color: accent }}>YOUTUBE_API_KEY</code> opcional usa la API oficial (más estable para alto volumen).</li>
          <li><strong>Telegram</strong>: scrapea <code style={{ fontFamily: mono, color: accent }}>t.me/s/&lt;canal&gt;</code>. Sin comentarios (Telegram no los expone en web).</li>
          <li><strong>FB / LinkedIn / X</strong>: vía <code style={{ fontFamily: mono, color: accent }}>SCRAPINGBEE_KEY</code> (free tier 1000 req/mes). Posts limitados, sin comentarios.</li>
          <li>El cron <code style={{ fontFamily: mono, color: accent }}>refresh-social</code> corre <strong>diario a las 03 ART</strong>. El botón <FaEdit style={{ verticalAlign: 'middle' }} /> permite cargar followers manualmente.</li>
        </ul>
      </div>
    </>
  );
};

export default SocialPanel;
